import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, TabNav, Button } from '../../../components/common';
import SEO, { buildFaqJsonLd } from '../../../components/common/SEO';
import { faqService } from '../../../api/services';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';

function AccordionItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-mono-100">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="text-sm font-semibold text-mono-900 pr-4 break-keep">{question}</span>
        <Icon
          name={isOpen ? 'chevron-up' : 'chevron-down'}
          size={20}
          className="text-mono-400 shrink-0"
        />
      </button>
      {isOpen && (
        <div className="pb-5 -mt-1">
          <p className="text-sm text-mono-500 leading-relaxed whitespace-pre-line break-keep">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [faqTabs, setFaqTabs] = useState([]);
  const [faqData, setFaqData] = useState([]);

  useEffect(() => {
    faqService.getCategories().then((cats) => setFaqTabs(cats)).catch(() => {});
    faqService.getList().then((data) => {
      setFaqData(data.map((f) => ({
        id: f.id,
        category: f.category,
        question: f.question,
        answer: f.answer,
      })));
    }).catch(() => {});
  }, []);

  const filteredFaq =
    activeTab === 'all'
      ? faqData
      : faqData.filter((item) => item.category === activeTab);

  // O: FAQ 질문/답변/카테고리 탭 라벨(백엔드 한국어) → DeepL 번역
  const tabLabelsBundle = useMemo(() => faqTabs.map((c) => c.label || ''), [faqTabs]);
  const tTabLabels = useDeepLTranslated(tabLabelsBundle);
  const tFaqTabs = faqTabs.map((c, i) => ({ ...c, label: tTabLabels[i] || c.label }));

  const faqTextsBundle = useMemo(() => {
    const out = [];
    filteredFaq.forEach((f) => { out.push(f.question || '', f.answer || ''); });
    return out;
  }, [filteredFaq]);
  const tFaqTexts = useDeepLTranslated(faqTextsBundle);
  const tFilteredFaq = filteredFaq.map((f, i) => ({
    ...f,
    question: tFaqTexts[i * 2] || f.question,
    answer: tFaqTexts[i * 2 + 1] || f.answer,
  }));

  const handleToggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO route="/faq" jsonLd={buildFaqJsonLd(tFilteredFaq)} />
      <div className="max-w-content mx-auto px-2.5 sm:px-5 py-6 sm:py-8 pb-16 sm:pb-8">
        <h1 className="text-xl font-semibold text-mono-900 mb-4">{t('info.faq_title')}</h1>

        {/* 카테고리 탭 — 자주묻는질문 제목 바로 아래, 어드민과 동일한 admin variant 사용 */}
        <TabNav tabs={tFaqTabs} activeTab={activeTab} onTabChange={setActiveTab} variant="admin" className="mb-4" />

        <div>
          {tFilteredFaq.map((item) => (
            <AccordionItem
              key={item.id}
              question={item.question}
              answer={item.answer}
              isOpen={openId === item.id}
              onToggle={() => handleToggle(item.id)}
            />
          ))}

          {filteredFaq.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-sm text-mono-400">{t('info.faq_no_category')}</p>
            </div>
          )}
        </div>

        {/* 문의하기 CTA → /inquiry로 이동 */}
        <div className="mt-10 border-t border-mono-100 pt-10 text-center">
          <h2 className="text-lg font-bold text-mono-900 mb-2">{t('info.faq_cta_title')}</h2>
          <p className="text-sm text-mono-500 mb-6">{t('info.faq_cta_desc')}</p>
          <Link to="/inquiry">
            <Button level="level1">
              <Icon name="faq" size={16} className="mr-1.5" />
              {t('info.faq_cta_button')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
